/**
 * /admin/rms — Admin "Named RMs" card (C2). The firm-wide home for Relationship-
 * Manager management, relocating it off the per-agreement RMs tab (C3 removes that
 * tab). A sorted, filterable table of ALL alive agreements with their RM set; each
 * row's "Manage" opens a set-editor that adds / revokes / re-tiers RMs.
 *
 * Commit model (§3g Pattern B): there is NO agreement-level Save here, so each op
 * commits IMMEDIATELY (Admin auto-applies via the maker-checker path — no CR queue
 * in practice). This deliberately differs from the per-agreement NamedRMSection,
 * which STAGED onto the Agreement page's Save. Feed: GET /agreement-users/firm-wide
 * (named_rm.edit-gated, C1). Writes: the existing agreement-users create/update/delete.
 */
import {
  ActionIcon, Alert, Anchor, Badge, Button, Container, Divider, Group, Loader, Modal, MultiSelect,
  SegmentedControl, Select, Stack, Switch, Table, Text, TextInput, Title, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle, IconCheck, IconSearch, IconStar, IconStarFilled, IconUnlink,
  IconUserPlus, IconUsers,
} from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { agreementUsersApi, type FirmWideRmRow } from '../api/agreementUsers';
import { AccessDeniedAlert } from '../components/RequireAccess';
import { BackToCompliance } from '../components/BackToCompliance';
import { useCurrentUser } from '../hooks/useAuth';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useUsers } from '../hooks/useUsers';

const FEED_KEY = ['agreement-users', 'firm-wide'] as const;

function StatusBadge({ status }: { status: string }) {
  const color = status === 'Active' ? 'green' : status === 'Prospect' ? 'grape' : 'gray';  // P37: blue means "pending approval"
  return <Badge color={color} variant="light">{status}</Badge>;
}

function RmChips({ row }: { row: FirmWideRmRow }) {
  if (row.rms.length === 0) return <Text size="sm" c="dimmed">— none —</Text>;
  return (
    <Group gap={6}>
      {row.rms.map((rm) => (
        <Badge
          key={rm.assignment_id}
          variant={rm.is_primary ? 'filled' : 'light'}
          color={rm.is_primary ? 'yellow' : 'gray'}
          leftSection={rm.is_primary ? <IconStarFilled size={10} /> : undefined}
        >
          {rm.user_display_name}
        </Badge>
      ))}
    </Group>
  );
}

// ── The set-editor modal — live per-agreement RM list, immediate-commit ops ──
function ManageRmModal({ row, onClose }: { row: FirmWideRmRow; onClose: () => void }) {
  const qc = useQueryClient();
  const usersQ = useUsers({ limit: 200 });
  const listQ = useQuery({
    queryKey: ['rms-manage', row.agreement_id],
    queryFn: () => agreementUsersApi.list({ agreement_id: row.agreement_id, limit: 100 }),
  });
  const [pickedUser, setPickedUser] = useState<string>('');
  const [pickedPrimary, setPickedPrimary] = useState(false);
  const [busy, setBusy] = useState(false);

  const rms = listQ.data?.items ?? [];
  const assignedIds = new Set(rms.map((a) => a.user_id));
  const pickerData = (usersQ.data?.items ?? [])
    .filter((u) => !assignedIds.has(u.id) && !u.deleted_at)
    .map((u) => ({ value: u.id, label: `${u.display_name || u.email} (${u.role})` }));

  const label = `${row.agreement_code ?? '(prospect)'} · ${row.agreement_mnemonic}`;

  async function run(fn: () => Promise<unknown>, ok: string) {
    setBusy(true);
    try {
      await fn();
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['rms-manage', row.agreement_id] }),
        qc.invalidateQueries({ queryKey: FEED_KEY }),
      ]);
      notifications.show({ color: 'green', icon: <IconCheck size={16} />, title: 'Saved', message: ok });
    } catch (e) {
      notifications.show({
        color: 'red', icon: <IconAlertCircle size={16} />, title: 'Failed',
        message: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setBusy(false);
    }
  }

  async function add() {
    if (!pickedUser) return;
    const uname = pickerData.find((o) => o.value === pickedUser)?.label ?? 'user';
    await run(
      () => agreementUsersApi.create({ agreement_id: row.agreement_id, user_id: pickedUser, is_primary: pickedPrimary }),
      `Assigned ${uname}${pickedPrimary ? ' (Primary)' : ''}.`,
    );
    setPickedUser('');
    setPickedPrimary(false);
  }

  return (
    <Modal opened onClose={onClose} centered size="lg" title={<Text fw={600}>Named RMs — {label}</Text>}>
      <Stack>
        {listQ.isLoading ? <Loader size="sm" /> : rms.length === 0 ? (
          <Text size="sm" c="dimmed">No Named RMs assigned yet.</Text>
        ) : (
          <Stack gap="xs">
            {rms.map((a) => (
              <Group key={a.id} justify="space-between" wrap="nowrap">
                <Group gap="xs">
                  <Text size="sm">{a.user_display_name ?? a.user_id.slice(0, 8)}</Text>
                  {a.is_primary
                    ? <Badge size="sm" color="yellow" variant="filled" leftSection={<IconStarFilled size={10} />}>Primary</Badge>
                    : <Badge size="sm" color="gray" variant="light">Secondary</Badge>}
                </Group>
                <Group gap={4} wrap="nowrap">
                  <Tooltip label={a.is_primary ? 'Demote to Secondary' : 'Promote to Primary'} withArrow>
                    <ActionIcon
                      variant="light" color={a.is_primary ? 'gray' : 'yellow'} disabled={busy}
                      onClick={() => run(
                        () => agreementUsersApi.update(a.id, { is_primary: !a.is_primary }),
                        `${a.user_display_name ?? 'RM'} is now ${a.is_primary ? 'Secondary' : 'Primary'}.`,
                      )}
                    >
                      <IconStar size={14} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Revoke assignment" withArrow>
                    <ActionIcon
                      variant="light" color="red" disabled={busy}
                      onClick={() => run(
                        () => agreementUsersApi.delete(a.id),
                        `Revoked ${a.user_display_name ?? 'RM'}.`,
                      )}
                    >
                      <IconUnlink size={14} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Group>
            ))}
          </Stack>
        )}

        <Divider label="Assign a Relationship Manager" labelPosition="center" />
        <Group align="flex-end">
          <Select
            label="User"
            placeholder={pickerData.length === 0 ? 'No users available' : 'Pick a user…'}
            data={pickerData}
            value={pickedUser}
            onChange={(v) => setPickedUser(v ?? '')}
            searchable
            clearable
            disabled={pickerData.length === 0 || busy}
            style={{ flex: 1 }}
          />
          <Tooltip label="Primary tier (multiple primaries allowed)" withArrow>
            <Switch label="Primary" checked={pickedPrimary} onChange={(e) => setPickedPrimary(e.currentTarget.checked)} />
          </Tooltip>
          <Button leftSection={<IconUserPlus size={14} />} onClick={add} disabled={!pickedUser || busy} loading={busy}>
            Assign
          </Button>
        </Group>
        {/* The fee split for RM commissions moved to Admin → RM commissions → Fee split
            (Pedro 2026-10-10: decoupled from Named RMs — any user may be credited). */}
      </Stack>
    </Modal>
  );
}

export function RmsPage() {
  useDocumentTitle('Named RMs');
  const { user } = useCurrentUser();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [rmFilter, setRmFilter] = useState('all');
  const [rmUsers, setRmUsers] = useState<string[]>([]);
  const [manageRow, setManageRow] = useState<FirmWideRmRow | null>(null);

  const feedQ = useQuery({ queryKey: FEED_KEY, queryFn: () => agreementUsersApi.firmWide() });

  if (!user) return null;
  if (user.role !== 'admin') return <AccessDeniedAlert />;

  const q = search.trim().toLowerCase();
  // "Filter by RM" options = the distinct RMs actually named across the book.
  const rmOptions = (() => {
    const m = new Map<string, string>();
    for (const r of feedQ.data ?? []) for (const rm of r.rms) m.set(rm.user_id, rm.user_display_name);
    return [...m.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  })();
  const rows = (feedQ.data ?? []).filter((r) => {
    if (q && !(`${r.agreement_code ?? ''} ${r.agreement_mnemonic}`.toLowerCase().includes(q))) return false;
    if (statusFilter !== 'all' && r.agreement_status !== statusFilter) return false;
    if (rmFilter === 'has' && r.rms.length === 0) return false;
    if (rmFilter === 'none' && r.rms.length > 0) return false;
    if (rmUsers.length && !r.rms.some((rm) => rmUsers.includes(rm.user_id))) return false;
    return true;
  });

  return (
    <Container size="xl">
      <Stack>
        <BackToCompliance to="/admin" label="Back to Admin" />
        <Group gap="xs" align="center">
          <IconUsers size={22} />
          <Title order={2}>Named RMs</Title>
        </Group>
        <Text size="sm" c="dimmed">
          Assign and manage Relationship Managers across all agreements. Changes apply
          immediately. Each agreement's primary RM also shows on its header and in the
          Agreements list.
        </Text>

        <Group align="flex-end" wrap="wrap" gap="md">
          <TextInput
            label="Search" placeholder="Code or mnemonic" leftSection={<IconSearch size={14} />}
            value={search} onChange={(e) => setSearch(e.currentTarget.value)} maw={260}
          />
          <div>
            <Text size="xs" fw={500} mb={4}>Status</Text>
            <SegmentedControl
              size="xs" value={statusFilter} onChange={setStatusFilter}
              data={[
                { label: 'All', value: 'all' }, { label: 'Active', value: 'Active' },
                { label: 'Inactive', value: 'Inactive' }, { label: 'Prospect', value: 'Prospect' },
              ]}
            />
          </div>
          <div>
            <Text size="xs" fw={500} mb={4}>RMs</Text>
            <SegmentedControl
              size="xs" value={rmFilter} onChange={setRmFilter}
              data={[
                { label: 'All', value: 'all' }, { label: 'Has RM', value: 'has' },
                { label: 'No RM', value: 'none' },
              ]}
            />
          </div>
          <MultiSelect
            label="Filter by RM" placeholder={rmUsers.length ? undefined : 'Any RM'} data={rmOptions}
            value={rmUsers} onChange={setRmUsers}
            searchable clearable maw={320}
          />
        </Group>

        {feedQ.isLoading ? <Loader size="sm" /> : feedQ.error ? (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            Could not load agreements. {feedQ.error instanceof Error ? feedQ.error.message : ''}
          </Alert>
        ) : (
          <>
            <Text size="xs" c="dimmed">{rows.length} agreement{rows.length === 1 ? '' : 's'}</Text>
            <Table withTableBorder striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Agreement</Table.Th>
                  <Table.Th w={110}>Status</Table.Th>
                  <Table.Th>Named RMs</Table.Th>
                  <Table.Th w={120} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((r) => (
                  <Table.Tr key={r.agreement_id}>
                    <Table.Td>
                      <Anchor component={Link} to={`/agreements/${r.agreement_id}`} size="sm" fw={500}>
                        {r.agreement_code ?? '(prospect)'}
                      </Anchor>
                      <Text size="xs" c="dimmed">{r.agreement_mnemonic}</Text>
                    </Table.Td>
                    <Table.Td><StatusBadge status={r.agreement_status} /></Table.Td>
                    <Table.Td><RmChips row={r} /></Table.Td>
                    <Table.Td>
                      <Button size="xs" variant="light" onClick={() => setManageRow(r)}>Manage</Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {rows.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={4}><Text size="sm" c="dimmed" ta="center" py="md">No agreements match the filters.</Text></Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </>
        )}
      </Stack>

      {manageRow && <ManageRmModal row={manageRow} onClose={() => setManageRow(null)} />}
    </Container>
  );
}
